import AvailabilityMapBlock from '@root/joinroot.com/src/components/blocks/availability-map-block';
import BlogHeaderSectionBlock from '@root/joinroot.com/src/components/blocks/blog-header-section-block';
import BlogRelatedPostsSection from '@root/joinroot.com/src/components/blocks/blog-related-posts-section-block';
import CustomReactComponentBlock from '@root/joinroot.com/src/components/blocks/custom-react-component-block';
import DisclaimerSectionBlock from '@root/joinroot.com/src/components/blocks/disclaimer-section-block';
import ErrorBoundary from '@root/core/src/components/error-boundary';
import ExpandableSectionBlock from '@root/joinroot.com/src/components/blocks/expandable-section-block';
import FaqSectionBlock from '@root/joinroot.com/src/components/blocks/faq-section-block';
import HeroSectionBlock from '@root/joinroot.com/src/components/blocks/hero-section-block';
import HeroSimpleSectionBlock from '@root/joinroot.com/src/components/blocks/hero-simple-section-block';
import IconToutGridBlock from '@root/joinroot.com/src/components/blocks/icon-tout-grid-block';
import IconToutSectionBlock from '@root/joinroot.com/src/components/blocks/icon-tout-section-block';
import IconToutSideBySideSectionBlock from '@root/joinroot.com/src/components/blocks/icon-tout-side-by-side-section-block';
import ImageToutSectionBlock from '@root/joinroot.com/src/components/blocks/image-tout-section-block';
import ImageToutSideBySideBlock from '@root/joinroot.com/src/components/blocks/image-tout-side-by-side-block';
import ImageToutSideBySideSectionBlock from '@root/joinroot.com/src/components/blocks/image-tout-side-by-side-section-block';
import MixpanelDetails from '@root/joinroot.com/src/components/mixpanel-details';
import PressSectionBlock from '@root/joinroot.com/src/components/blocks/press-section-block';
import PropTypes from '@root/vendor/prop-types';
import QuoteToutSectionBlock from '@root/joinroot.com/src/components/blocks/quote-tout-section-block';
import RichTextSectionBlock from '@root/joinroot.com/src/components/blocks/rich-text-section-block';
import SpacerBlock from '@root/joinroot.com/src/components/blocks/spacer-block';
import VideoToutSectionBlock from '@root/joinroot.com/src/components/blocks/video-tout-section-block';
import environment from '@root/core/src/utils/environment';
import snakeCase from '@root/vendor/lodash/snakeCase';
import useContentfulVariationContainer from '@root/joinroot.com/src/hooks/use-contentful-variation-container';
import useVisibilityAnalytics from '@root/core/src/hooks/use-visibility-analytics';
import { useEffect, useState } from '@root/vendor/react';

const BlockComponents = {
  'SPACER': SpacerBlock,
  'BLOG_HEADER_SECTION': BlogHeaderSectionBlock,
  'BLOG_RELATED_POSTS_SECTION': BlogRelatedPostsSection,
  'CUSTOM_REACT_COMPONENT': CustomReactComponentBlock,
  'DISCLAIMER_SECTION': DisclaimerSectionBlock,
  'HERO': HeroSectionBlock,
  'HERO_SIMPLE': HeroSimpleSectionBlock,
  'RICH_TEXT_SECTION': RichTextSectionBlock,
  'EXPANDABLE_SECTION': ExpandableSectionBlock,
  'FAQ_SECTION': FaqSectionBlock,
  'IMAGE_TOUT_SIDE_BY_SIDE': ImageToutSideBySideBlock,
  'ICON_TOUT_SIDE_BY_SIDE': IconToutSideBySideSectionBlock,
  'ICON_TOUT_SECTION': IconToutSectionBlock,
  'PRESS_SECTION': PressSectionBlock,
  'IMAGE_TOUT_SECTION': ImageToutSectionBlock,
  'IMAGE_TOUT_SIDE_BY_SIDE_SECTION': ImageToutSideBySideSectionBlock,
  'ICON_TOUT_GRID': IconToutGridBlock,
  'VIDEO_TOUT_SECTION': VideoToutSectionBlock,
  'QUOTE_TOUT_SECTION': QuoteToutSectionBlock,
  'AVAILABILITY_MAP': AvailabilityMapBlock,
};

const VARIATION_CONTAINER = 'VARIATION_CONTAINER';

const formattedContentId = (blockToFormat) => {
  return snakeCase(blockToFormat?.sys?.contentType?.sys?.id)?.toUpperCase();
};

function BlockRenderer({ block }) {
  const [blockToUse, setBlockToUse] = useState(block);
  const [loaded, setLoaded] = useState(false);
  const newBlockToUse = useContentfulVariationContainer(block);
  const isVariation = formattedContentId(block) === VARIATION_CONTAINER;
  const contentId = formattedContentId(blockToUse);

  useEffect(() => {
    async function fetchData() {
      if (!isVariation) { return; }
      setBlockToUse(newBlockToUse);
      setLoaded(true);
    }

    fetchData();
  }, [isVariation, newBlockToUse]);

  const analyticsContext = `${contentId}_${block.id}`;
  const {
    nodeRef,
    trackClick,
    trackEvent,
  } = useVisibilityAnalytics(analyticsContext);
  if (!contentId) {
    return null;
  }
  const ComponentToRender = BlockComponents[contentId];

  if (!ComponentToRender && (!isVariation || loaded)) {
    throw new Error(`No block component found to render ${formattedContentId(blockToUse)} content type`);
  }

  return (
    <div>
      {(!isVariation || loaded) &&
        <div
          data-testid={'block-renderer'}
          ref={nodeRef}
        >
          <MixpanelDetails
            analyticsContext={analyticsContext}
            isPageView={false}
          />

          <ComponentToRender
            content={blockToUse}
            trackClick={trackClick}
            trackEvent={trackEvent}
          />
        </div>

      }
    </div>
  );
}

export default function BlockRendererWrapper(props) {
  const isInPreviewMode = environment.contentfulPreviewEnabled;

  return isInPreviewMode
    ? <ErrorBoundary showErrorScreen={false}><BlockRenderer {...props} /></ErrorBoundary>
    : <BlockRenderer {...props} />;
}

BlockRenderer.propTypes = {
  block: PropTypes.shape({
    id: PropTypes.string.isRequired,
    content: PropTypes.object,
    sys: PropTypes.shape({
      contentType: PropTypes.shape({
        sys: PropTypes.shape({
          id: PropTypes.string.isRequired,
        }),
      }),
    }).isRequired,
  }).isRequired,
};
